/**
 * Виджет «Способы закупки» (FR-6): круговая диаграмма + счётчик
 * конкурсной доли в центре, переключатель «Сумма · план / Сумма · факт /
 * Кол-во» (FR-6.2). Конкурс vs из одного источника (FR-6.1).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DonutChart } from './DonutChart';
import { CHART_COLORS, fmt, moneyMln } from './format';
import { SegToggle } from './SegToggle';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import type { ProcurementSlice } from '../../../types/projects';

type Mode = 'plan' | 'fact' | 'count';

interface ProcurementWidgetProps {
  procurement: ProcurementSlice[];
}

export const ProcurementWidget = ({ procurement }: ProcurementWidgetProps) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('plan');

  const valueOf = (slice: ProcurementSlice): number => {
    if (mode === 'count') return slice.contractsCount ?? 0;
    return (mode === 'plan' ? slice.planSumMln : slice.factSumMln) ?? 0;
  };

  const valueLabel = (v: number): string => {
    if (mode === 'count') {
      return `${fmt(v)} ${t('projects.units.contractsShort')}`;
    }
    const money = moneyMln(v);
    return `${money.value} ${t(money.mlrd ? 'projects.units.mlrdTg' : 'projects.units.mlnTg')}`;
  };

  const items = procurement.map((slice, index) => ({
    key: slice.key,
    label: t(`projects.infrastructure.procurementNames.${slice.key}Full`, {
      defaultValue: t(`projects.infrastructure.procurementNames.${slice.key}`, {
        defaultValue: slice.key,
      }),
    }),
    value: valueOf(slice),
    color: CHART_COLORS[index % CHART_COLORS.length],
    valueLabel: valueLabel(valueOf(slice)),
  }));

  // Счётчик в центре — доля конкурсных договоров/сумм (FR-6.1).
  const total = items.reduce((acc, item) => acc + item.value, 0);
  const tender = items.find((item) => item.key === 'tender')?.value ?? 0;
  const share = total > 0 ? Math.round((tender / total) * 100) : 0;

  return (
    <Card className="shadow-card" data-testid="projects-procurement">
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle>{t('projects.infrastructure.procurement.title')}</CardTitle>
        <SegToggle<Mode>
          ariaLabel={t('projects.infrastructure.procurement.title')}
          options={[
            { value: 'plan', label: t('projects.infrastructure.seg.sumPlan') },
            { value: 'fact', label: t('projects.infrastructure.seg.sumFact') },
            { value: 'count', label: t('projects.infrastructure.seg.count') },
          ]}
          value={mode}
          onChange={setMode}
          testId="projects-procurement-mode"
        />
      </CardHeader>
      <CardContent>
        <DonutChart
          items={items}
          centerValue={`${share}%`}
          centerLabel={`${t('projects.infrastructure.procurement.tenderShare')} · ${t(
            `projects.infrastructure.procurement.by.${mode}`,
          )}`}
          testId="projects-procurement-donut"
        />
      </CardContent>
    </Card>
  );
};
